import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router";

import {
  type AcquireOutcome,
  acquireRecoveryCredential,
  adoptHeldRecovery,
  consumeRecoveryRequested,
  type HeldRecovery,
  isPasswordRecoveryUrl,
  MIN_RECOVERY_PASSWORD_LENGTH,
  updatePasswordAndSignOut,
} from "../auth/password-recovery";
import { parseRecoveryLink, type RecoveryLink } from "../auth/recovery-credential";
import { createAdminSupabaseClient, createEphemeralAdminSupabaseClient } from "../auth/supabase";
import { AsyncState } from "../ui/async-state";
import { Button } from "../ui/button";
import { StatusMessage } from "../ui/status-message";
import { AuthField, AuthShell, PasswordInput } from "./auth-parts";

// The admin site is the Supabase Site URL, so this page also receives the recovery link of a CUSTOMER (CHT-1628 item 6). The decision:
// the recovery form is NOT behind the staff check. It makes no `/staff/*` call and loads no admin data; it only sets a new password,
// ends the recovery session everywhere and tells the person to go back to the app. Any other session that lands here and is not staff is
// signed out by the staff check with a notice (`access-denied-notice.ts`). A customer gets no admin access at any point.
//
// A recovery link is a credential (CHT-1628 review). Its tokens are checked (subject, age under 15 minutes, a one-time-link method, then the
// server) and HELD; they become the session only when the form is submitted, and a session of another account in this browser is signed out
// first. The form says whose account it is. A link that is refused ends on the error page: the page never falls back to a session that was
// there before. Residual risk: docs/engineering/admin-ia.md section 14.4.

export function AuthCallbackPage() {
  const client = useMemo(() => createAdminSupabaseClient(), []);
  const [status, setStatus] = useState<"loading" | "recovery" | "changed" | "complete" | "error">(
    "loading",
  );
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const markerRef = useRef<boolean | null>(null);
  // The link, read once. The fragment of an implicit-flow link holds the session tokens: it is read and removed from the address bar
  // in the first run of the effect, and a repeated run (StrictMode) must still see what the first one saw.
  const linkRef = useRef<{ recoveryUrl: boolean; link: RecoveryLink } | null>(null);
  const heldRef = useRef<Promise<AcquireOutcome> | null>(null);
  // The credential that was checked and is held (shown as the masked e-mail; made the session at submit).
  const [held, setHeld] = useState<HeldRecovery | null>(null);
  // Captured at first render: auth-js removes `code` from the URL once the exchange completes.
  const [hasAuthCode] = useState(() => new URL(window.location.href).searchParams.has("code"));

  useEffect(() => {
    if (!client) {
      setStatus("error");
      return;
    }
    let mounted = true;
    // A PKCE recovery link carries only `?code=` and auth-js reports PASSWORD_RECOVERY late, so the
    // signals are: the marker left by this browser's reset request, the event, or a type=recovery URL.
    // Consumed once per page load; StrictMode re-runs this effect and must not lose the marker.
    markerRef.current ??= consumeRecoveryRequested(window.localStorage);
    if (linkRef.current === null) {
      linkRef.current = {
        recoveryUrl: isPasswordRecoveryUrl(window.location.href),
        link: parseRecoveryLink(window.location.href),
      };
      // At once, before anything is awaited: the tokens and the one-time hash leave the address bar and the history.
      if (linkRef.current.link.kind !== "none") {
        window.history.replaceState(window.history.state, "", window.location.pathname);
      }
    }
    const { recoveryUrl, link } = linkRef.current;
    if (link.kind !== "none") {
      // The credential of the link is checked and HELD. There is no fallback: whatever session this browser holds is never used for the
      // form, and a refused link ends on the error page.
      heldRef.current ??= acquireRecoveryCredential(
        client,
        createEphemeralAdminSupabaseClient,
        link,
        Math.floor(Date.now() / 1000),
      ).catch((): AcquireOutcome => ({ ok: false, reason: "SERVER_REFUSED" }));
      void heldRef.current.then((outcome) => {
        if (!mounted) return;
        if (outcome.ok) {
          setHeld(outcome.held);
          setStatus("recovery");
        } else {
          setStatus("error");
        }
      });
      return () => {
        mounted = false;
      };
    }
    // The marker only counts on a callback that carries an auth code, so a stale marker cannot turn
    // an ordinary sign-in callback into the recovery form.
    // `type=recovery` alone proves nothing: it counts with a code that the client exchanged (or with the event of auth-js).
    let recoverySignalled = hasAuthCode && (recoveryUrl || markerRef.current);
    const { data } = client.auth.onAuthStateChange((event) => {
      if (event !== "PASSWORD_RECOVERY") return;
      recoverySignalled = true;
      if (mounted) setStatus("recovery");
    });
    void client.auth.getSession().then(({ data: sessionData, error: sessionError }) => {
      if (!mounted) return;
      // Never offer the new-password form without a session: updateUser would fail with an
      // opaque error (e.g. a link the PKCE client could not exchange).
      if (!sessionData.session || sessionError) {
        setStatus("error");
      } else {
        setStatus(recoverySignalled ? "recovery" : "complete");
      }
    });
    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, [client, hasAuthCode]);

  if (status === "loading") {
    return (
      <main className="grid min-h-dvh place-items-center bg-background px-4 py-10">
        <AsyncState
          kind="loading"
          title="Completing authentication..."
          message="Checking the secure callback."
        />
      </main>
    );
  }
  if (status === "recovery" && client) {
    return (
      <AuthShell titleId="recovery-title" title="Set a new password">
        {held ? (
          <p className="m-0 text-base leading-7 text-foreground">
            Set a new password for {held.maskedEmail}
          </p>
        ) : null}
        <p className="m-0 text-sm leading-6 text-muted-foreground">
          This secure recovery session is separate from staff-console authorization.
        </p>
        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (password.length < MIN_RECOVERY_PASSWORD_LENGTH || password !== confirmation) {
              setError(
                `Use at least ${MIN_RECOVERY_PASSWORD_LENGTH} characters and enter the same password twice.`,
              );
              return;
            }
            setPending(true);
            setError(null);
            void (async () => {
              if (held) {
                // The held credential replaces the session of this browser only now; another account's session is signed out first.
                const adopted = await adoptHeldRecovery(client, held);
                if (!adopted) {
                  setHeld(null);
                  setStatus("error");
                  return;
                }
              }
              return updatePasswordAndSignOut(client, password);
            })()
              .then((result) => {
                if (result === undefined) return;
                if (!result.success) {
                  setError(result.message ?? "The new password could not be saved.");
                  setPending(false);
                  return;
                }
                setPassword("");
                setConfirmation("");
                setStatus("changed");
              })
              .catch(() => {
                setError("The new password could not be saved. Check your connection and retry.");
                setPending(false);
              });
          }}
        >
          <AuthField
            id="recovery-password"
            label="New password"
            hint={`At least ${MIN_RECOVERY_PASSWORD_LENGTH} characters.`}
          >
            <PasswordInput
              id="recovery-password"
              name="new-password"
              autoComplete="new-password"
              minLength={MIN_RECOVERY_PASSWORD_LENGTH}
              value={password}
              onChange={setPassword}
              showLabel="Show new password"
              describedBy="recovery-password-hint"
            />
          </AuthField>
          <AuthField id="recovery-confirmation" label="Confirm new password">
            <PasswordInput
              id="recovery-confirmation"
              name="confirm-new-password"
              autoComplete="new-password"
              minLength={MIN_RECOVERY_PASSWORD_LENGTH}
              value={confirmation}
              onChange={setConfirmation}
              showLabel="Show confirmed password"
            />
          </AuthField>
          {error ? <StatusMessage message={{ tone: "error", text: error }} /> : null}
          <Button type="submit" size="lg" className="w-full" loading={pending}>
            {pending ? "Saving..." : "Save new password"}
          </Button>
        </form>
      </AuthShell>
    );
  }
  if (status === "changed") {
    return (
      <AuthShell titleId="changed-title" title="Password changed">
        <StatusMessage
          message={{ tone: "success", text: "Password changed. Return to the app and sign in." }}
        />
        <Button asChild variant="outline">
          <Link to="/login">Staff console sign-in</Link>
        </Button>
      </AuthShell>
    );
  }
  if (status === "complete") return <Navigate to="/home" replace />;
  return (
    <AuthShell titleId="callback-error-title" title="Link could not be used">
      <StatusMessage
        message={{
          tone: "error",
          text: "This link is invalid or has expired. Request a new reset email from the app (customers) or from the sign-in page (staff).",
        }}
      />
      <Button asChild variant="outline">
        <Link to="/login">Back to sign in</Link>
      </Button>
    </AuthShell>
  );
}
